import type { ItemStackNbt, PotionBitfield } from "../models/itemStack";

export function decodePotionDamage(damage: number): PotionBitfield {
    return {
        empty: damage === 0,
        effectId: damage & 0xF,
        tierII: (damage & 0x20) !== 0,
        extended: (damage & 0x40) !== 0,
        splash: (damage & 0x4000) !== 0,
    };
}

export const POTION_EFFECT_KEYS: Record<number, string> = {
    0: "awkward",
    1: "regeneration",
    2: "swiftness",
    3: "fire_resistance",
    4: "poison",
    5: "healing",
    6: "night_vision",
    7: "clear",
    8: "weakness",
    9: "strength",
    10: "slowness",
    11: "leaping",
    12: "harming",
    13: "water_breathing",
    14: "invisibility",
    15: "thick",
};

export function potionEffectKey(bitfield: PotionBitfield): string {
    return POTION_EFFECT_KEYS[bitfield.effectId] ?? "unknown";
}

// id and liquidColor off each 1.8.9 net.minecraft.potion.Potion. A CustomPotionEffects entry names its
// effect by that id, and brewing cannot reach most of them, so the table covers all 23 rather than the
// thirteen a brewing stand produces.
const POTION_TYPES: Record<number, { key: string; liquidColor: number }> = {
    1: { key: "swiftness", liquidColor: 8171462 },
    2: { key: "slowness", liquidColor: 5926017 },
    3: { key: "haste", liquidColor: 14270531 },
    4: { key: "mining_fatigue", liquidColor: 4866583 },
    5: { key: "strength", liquidColor: 9643043 },
    6: { key: "healing", liquidColor: 16262179 },
    7: { key: "harming", liquidColor: 4393481 },
    8: { key: "leaping", liquidColor: 2293580 },
    9: { key: "nausea", liquidColor: 5578058 },
    10: { key: "regeneration", liquidColor: 13458603 },
    11: { key: "resistance", liquidColor: 10044730 },
    12: { key: "fire_resistance", liquidColor: 14981690 },
    13: { key: "water_breathing", liquidColor: 3035801 },
    14: { key: "invisibility", liquidColor: 8356754 },
    15: { key: "blindness", liquidColor: 2039587 },
    16: { key: "night_vision", liquidColor: 2039713 },
    17: { key: "hunger", liquidColor: 5797459 },
    18: { key: "weakness", liquidColor: 4738376 },
    19: { key: "poison", liquidColor: 5149489 },
    20: { key: "wither", liquidColor: 3484199 },
    21: { key: "health_boost", liquidColor: 16284963 },
    22: { key: "absorption", liquidColor: 2445989 },
    23: { key: "saturation", liquidColor: 16262179 },
};

export const POTION_EFFECT_IDS = Object.keys(POTION_TYPES).map(Number);

// PotionHelper.potionRequirements tests bits 0 to 3 of the damage value, and every brewable potion
// claims exactly one of the sixteen nibbles. Nibbles 0, 7 and 15 brew no effect at all.
const BREWED_POTION_IDS: Record<number, number> = {
    1: 10,
    2: 1,
    3: 12,
    4: 19,
    5: 6,
    6: 16,
    8: 18,
    9: 5,
    10: 2,
    11: 8,
    12: 7,
    13: 13,
    14: 14,
};

// What PotionHelper.calcPotionLiquidColor answers for an empty effect list, which is the water a
// brewing stand starts from.
const WATER_LIQUID_COLOR = 3694022;

// calcPotionLiquidColor: an effect contributes its colour once per level it is amplified to, and the
// bottle renders the average of every contribution.
function mixLiquidColors(effects: readonly { id: number; amplifier: number }[]): number | null {
    let red = 0;
    let green = 0;
    let blue = 0;
    let levels = 0;
    for (const effect of effects) {
        const type = POTION_TYPES[effect.id];
        if (!type) continue;
        const amplifier = Math.max(0, Math.min(effect.amplifier, 255));
        for (let level = 0; level <= amplifier; level++) {
            red += ((type.liquidColor >> 16) & 255) / 255;
            green += ((type.liquidColor >> 8) & 255) / 255;
            blue += (type.liquidColor & 255) / 255;
            levels++;
        }
    }
    if (levels === 0) return null;
    return (Math.trunc(red / levels * 255) << 16)
        | (Math.trunc(green / levels * 255) << 8)
        | Math.trunc(blue / levels * 255);
}

export function potionEffectKeyById(potionId: number): string | undefined {
    return POTION_TYPES[potionId]?.key;
}

// ItemPotion.getItemStackDisplayName names a potion after the first effect it carries.
export function customPotionEffectKey(effects: ItemStackNbt["customPotionEffects"]): string | undefined {
    for (const effect of effects ?? []) {
        const type = POTION_TYPES[effect.id];
        if (type) return type.key;
    }
    return undefined;
}

// 1.8.9 tints the bottle off the damage value alone, so a potion handed out by command renders as
// water in game however many effects its NBT carries. The panel has to say what is in the bottle, so
// the NBT wins here where it does not in the client.
export function potionLiquidColor(
    bitfield: PotionBitfield,
    effects?: ItemStackNbt["customPotionEffects"],
): string {
    const mixed = effects && effects.length > 0 ? mixLiquidColors(effects) : null;
    const brewed = POTION_TYPES[BREWED_POTION_IDS[bitfield.effectId]];
    const rgb = mixed ?? (brewed ? brewed.liquidColor : WATER_LIQUID_COLOR);
    return `#${rgb.toString(16).padStart(6, "0")}`;
}
